import { useEffect, useState } from 'react';
import { Button, cn } from '@ircf/ui';
import { assetUrl, brandAssets, features } from '@ircf/config';
import {
  dismissInstallPrompt,
  isIosSafari,
  isMobileViewport,
  isStandaloneDisplay,
  wasInstallDismissedRecently,
  type BeforeInstallPromptEvent,
} from '../../lib/pwa.js';

/**
 * Mobile install prompt for the Progressive Web App.
 *
 * Android/Chrome: uses `beforeinstallprompt`.
 * iOS Safari: shows Add-to-Home-Screen instructions (no native prompt API).
 */
export function InstallPrompt({ className }: { className?: string }) {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (!features.pwa) return;
    if (isStandaloneDisplay()) return;
    if (wasInstallDismissedRecently()) return;
    if (!isMobileViewport()) return;

    if (isIosSafari()) {
      setIosHint(true);
      setVisible(true);
      return;
    }

    const onPrompt = (event: Event) => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  if (!features.pwa || !visible) return null;

  const close = () => {
    dismissInstallPrompt();
    setVisible(false);
    setDeferred(null);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    close();
  };

  return (
    <div
      role="dialog"
      aria-labelledby="pwa-install-title"
      aria-describedby="pwa-install-body"
      className={cn(
        'fixed inset-x-3 bottom-3 z-50 mx-auto max-w-md rounded-2xl border border-accent-pt/30 bg-graphite/95 p-4 shadow-[0_20px_60px_-20px_rgb(0_0_0_/0.8)] backdrop-blur-xl sm:inset-x-auto sm:right-4 sm:bottom-4',
        className,
      )}
    >
      <div className="flex gap-3">
        <img
          src={assetUrl(brandAssets.logoPrimary)}
          alt=""
          width={44}
          height={44}
          className="size-11 shrink-0 rounded-xl object-contain"
        />
        <div className="min-w-0 flex-1">
          <h2 id="pwa-install-title" className="font-condensed text-lg font-semibold tracking-wide text-warm-white">
            Install the app
          </h2>
          <p id="pwa-install-body" className="mt-1 text-sm leading-relaxed text-platinum/85">
            {iosHint
              ? 'On iPhone: tap Share, then “Add to Home Screen” for one-tap access to your plans and bookings.'
              : 'Add Iron Resolve Combat Fitness to your home screen for faster access and push reminders.'}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {!iosHint && deferred && (
              <Button type="button" size="sm" pathway="pt" onClick={() => void install()}>
                Install
              </Button>
            )}
            <Button type="button" size="sm" variant="secondary" onClick={close}>
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
